import React from 'react';
import { Award, Target, Users, ShieldCheck } from 'lucide-react';
import { aboutContent, teamMembers, siteInfo } from '../data/siteData';

const AboutPage = ({ onBookingClick }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-black text-white py-20">
        <div className="w-full px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">Our Story</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            {aboutContent.title}
          </h1>
          <p className="text-xl text-gray-300 w-full">
            {aboutContent.subtitle}
          </p>
        </div>
      </section>

      {/* Mission & Story */}
      <section className="py-20 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=800" 
                alt="LookWell Professional Grooming"
                className="w-full h-auto max-h-[450px] object-cover shadow-2xl rounded-lg"
              />
            </div>
            <div className="flex flex-col">
              <h2 className="text-4xl font-bold text-black mb-6">Our Story</h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                {aboutContent.description}
              </p>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                {aboutContent.mission}
              </p>
              <div>
                <button
                  onClick={onBookingClick}
                  className="bg-amber-500 text-black px-8 py-3 font-bold uppercase tracking-wider hover:bg-amber-600 hover:text-black transition-colors"
                >
                  Book Your Visit
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values - Why Choose LookWell */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-amber-50">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">Why Choose LookWell</p>
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              The LookWell Difference
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Where traditional barbering excellence meets modern luxury in the heart of Dubai
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {aboutContent.values.map((value, index) => (
              <div 
                key={index} 
                className="group relative bg-white p-8 shadow-md hover:shadow-2xl transition-all duration-500 border-t-4 border-amber-500 hover:border-amber-600"
              >
                {/* Icon with modern styling */}
                <div className="mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    {index === 0 && <Award size={32} className="text-white" />}
                    {index === 1 && <Target size={32} className="text-white" />}
                    {index === 2 && <ShieldCheck size={32} className="text-white" />}
                    {index === 3 && <Users size={32} className="text-white" />}
                  </div>
                </div>

                {/* Content */}
                <div className="text-center">
                  <h3 className="text-xl font-bold text-black mb-4 group-hover:text-amber-600 transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>

                {/* Subtle bottom decoration */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-amber-500 text-sm uppercase tracking-widest mb-4 font-medium">Our Expert Team</p>
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              Meet Our Haircut and Grooming Experts
            </h2>
            <p className="text-gray-600 text-lg w-full">
              Our team of internationally trained professionals brings years of experience and passion to every service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member) => (
              <div key={member.id} className="group">
                <div className="relative overflow-hidden mb-4">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                    <div className="text-white">
                      {member.specialties.map((specialty, idx) => (
                        <span key={idx} className="text-xs bg-amber-500 px-2 py-1 mr-1 mb-1 inline-block">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-black mb-1">{member.name}</h3>
                <p className="text-amber-500 font-medium mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden">
        {/* Background Image - Using a unique barbershop image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=1920)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-amber-900/75 to-black/85"></div>
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 text-center">
          {/* Decorative Line */}
          <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
            Experience the LookWell Difference
          </h2>
          <p className="text-xl md:text-2xl text-gray-200 mb-12">
            Join thousands of satisfied clients across Dubai
          </p>
          
          <button
            onClick={onBookingClick}
            className="group relative bg-amber-500 text-black px-12 py-5 text-lg font-bold uppercase tracking-wider overflow-hidden transition-all duration-300 hover:bg-amber-600 hover:scale-105 shadow-2xl"
          >
            <span className="relative z-10">Book Your Appointment</span>
            <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          </button>

          {/* Decorative circles */}
          <div className="absolute top-10 left-10 w-32 h-32 border-4 border-amber-500/30 rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-24 h-24 border-4 border-amber-500/30 rounded-full"></div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
